/* eslint-disable @typescript-eslint/no-extra-parens */
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Actions, ofType, createEffect, concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import {
    map, tap, catchError, switchMap, filter, first, mergeMap, take,
} from 'rxjs/operators';

import { SendHomePageView } from '../../../pages/home-ssr/store/home.actions';
import { EventFrom } from '../COMMON/models/analytics-common.models';
import { ConfigurationService } from '../COMMON/services/configuration.service';
import { sendObservabilityLog } from '../ITXObservabilityCore/helper';
import { EventData, EcommerceData } from '../models/general/general.models';
import { PageViewDataObject } from '../models/page-view/page-view.models';
import {
    imEventWrapperGetBasicCustomData,
    imEventWrapperGetBasicPageView,
    imEventWrapperGetPageView,
    imEventWrapperSendBasicAnalytics,
    imEventWrapperSendBasicPageView,
    imEventWrapperSendCustomAnalytics,
    imEventWrapperSendServerError,
} from '../services/event/im-event-wrapper.helper';
import { imEventAnalyticsEvent, imEventBasicEvent, imEventPageView } from '../services/event/im-event.helper';
import { imPopUpGetCategoryData, imPopUpSendBasic, imPopUpSendCustom } from '../services/popup/im-popup-service';
import { getPurchaseCategoryFromRoute } from '../services/purchase/im-purchase.service';
import { eventFormError } from '../services/tracking-common/im-tracking-common.helper';

import * as AnalyticsActions from './analytics.actions';
import * as AppEventsAnalyticsActions from './app-events-analytics.actions';
import { HomeLoadedAnalytics } from './app-events-analytics.actions';

import { Product } from '@api/catalog/products/products.models';
import { applicationErrorPagesMap } from '@core/domain/constants/application-pages.const';
import { Category } from '@core/domain/models/category.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { EcommerceContext } from '@core/domain/models/context.models';
import { LoadModule, SetPage } from '@core/infrastructure/state/core-commands.actions';
import { getContext, getCurrencyInfo, getIsModuleLoaded } from '@core/infrastructure/state/core.selectors';
import { CoreStorageService } from '@core/services/storage/storage.service';
import { CategoriesSelectors } from '@features/category';
import { SidebarCommands } from '@features/sidebar/infrastructure/commands/sidebar.command';
import { PublicUser } from '@features/user';
import { checkMatch } from '@frontend/common/utils';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { getCategory } from '@pages/category/store/category-page.selectors';
import { getCategoryProduct } from '@pages/product/store/product-page.selectors';
import { MenuCategory } from '@shared/header/models/category.model';
import { getCurrentOrder } from '@shared/order-payment/store/order-repayment.selectors';
import { getShopCart } from '@shared/shopcart/store/shopcart.selectors';
import { getShopCartCore } from '@shared/shopcart-core/store/shopcart-core.selectors';
import { getUser, getUserSelector, selectUserIsPrivateSale } from '@shared/user/store';

@Injectable()
export class AnalyticsEffects {
    private readonly getContext$: Observable<EcommerceContext> = this.store.select(getContext);

    private readonly getUser$: Observable<PublicUser> = this.store.select(getUserSelector);

    private readonly getCategoriesNavigation$: Observable<
    MenuCategory[]
    > = this.store.select(CategoriesSelectors.getCategoriesNavigation);

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly configurationService: ConfigurationService,
        @Inject(WINDOW) private readonly window: CommonWindow,
        private readonly storageService: CoreStorageService,
    ) { }

    public eventFormError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.EventFormErrorAnalytics),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            concatLatestFrom(() => this.store.select(routerPublicQueries.getUrlFromRouter)),
            map(([effectAction, currentRouteUrl]) => {
                const _categoryData = getPurchaseCategoryFromRoute(currentRouteUrl);
                const eventData = {
                    ...effectAction,
                    category: effectAction.eventFrom === EventFrom.checkout ? _categoryData.category : effectAction.category,
                    eventFrom: effectAction.eventFrom === EventFrom.checkout
                        ? _categoryData.eventFrom : effectAction.eventFrom,
                };
                return eventFormError(
                    eventData.cf,
                    eventData.category,
                    eventData.form,
                    eventData.formName,
                );
            }),
            filter((events) => !!events.length),
            mergeMap((events) => {
                const actions = [];
                events.forEach((event) => {
                    actions.push(
                        AnalyticsActions.AnalyticsEvent({
                            eventData: event,
                        })
                    );
                });
                return actions;
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public sendBasicAnalytics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AnalyticsActions.SendBasicAnalytics),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            concatLatestFrom(() => this.getUser$),
            tap(([effectAction, user]) => {
                const {
                    cf,
                    category,
                    action,
                    label,
                } = effectAction;
                const eventData: EventData = {
                    cf,
                    category,
                    action,
                    label,
                };
                imEventWrapperSendBasicAnalytics(eventData, user, this.window);
            }),
            map(([effectAction]) => {
                const { eventFrom } = effectAction;
                const dataObject = {};
                return AnalyticsActions.LoadEventSuccess({
                    payload: {
                        type: 'sendBasicAnalytics',
                        dataObject,
                        eventFrom,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public analyticsEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AnalyticsActions.AnalyticsEvent),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            concatLatestFrom(() => this.getUser$),
            map(([data, user]) => {
                const { eventData } = data;
                return {
                    eventData,
                    customData: imEventWrapperGetBasicCustomData(user, this.window),
                };
            }),
            tap(({ eventData, customData }) => {
                imEventAnalyticsEvent([{ eventData, customData }]);
            }),
            map(({ eventData }) => {
                const { eventFrom } = eventData;
                const dataObject = {};
                return AnalyticsActions.LoadEventSuccess({
                    payload: {
                        type: 'analyticsEvent',
                        dataObject,
                        eventFrom,
                    },
                });
            }),
        );
    });

    public sendCustomAnalitycsEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AnalyticsActions.SendCustomAnalytics),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            concatLatestFrom(() => this.store.select(getUser)),
            map(([effectAction, user]) => {
                try {
                    const { eventData, customData, eccomerce } = effectAction;

                    imEventWrapperSendCustomAnalytics(eventData, customData, user, this.window, eccomerce);

                    const dataObject = {};
                    return AnalyticsActions.LoadEventSuccess({
                        payload: {
                            type: 'customAnaliticsEvent',
                            dataObject,
                            eventFrom: effectAction.eventData.eventFrom,
                        },
                    });
                } catch (e) {
                    sendObservabilityLog(e, 'sendCustomAnalitycsEvent$');
                    return AnalyticsActions.LoadEventError();
                }
            }),
        );
    });

    public sendServerError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.SendServerErrorAnalytics),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            map((action) => {
                const {
                    cf, category, form, error, eventFrom,
                } = action;
                return { eventData: imEventWrapperSendServerError(cf, category, form, error), eventFrom };
            }),
            map(({ eventData, eventFrom }) => {
                return AnalyticsActions.AnalyticsEvent({
                    eventData: {
                        ...eventData,
                        eventFrom,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public sendBasicEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AnalyticsActions.BasicDataLayerEvent),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            tap((effectAction) => {
                const { event } = effectAction;
                imEventBasicEvent(event);
            }),
            map((effectAction) => {
                const { eventFrom } = effectAction;
                const dataObject = {};
                return AnalyticsActions.LoadEventSuccess({
                    payload: {
                        type: 'sendBasicEvent',
                        dataObject,
                        eventFrom,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public pageViewEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AnalyticsActions.PageViewEvent),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            switchMap((action) => {
                const comb1 = combineLatest([
                    this.getContext$,
                    this.getUser$,
                    this.store.select(getShopCart),
                    this.store.select(getShopCartCore),
                    of(action),
                ]);
                const comb2 = combineLatest([
                    this.store.select(getCurrentOrder),
                    this.store.select(routerPublicQueries.getUrlFromRouter),
                    this.store.select(getCurrencyInfo),
                ]);
                return combineLatest([
                    comb1,
                    comb2,
                ]).pipe(
                    filter(([[context, user, shopCart, shopCartCore]]) => {
                        return (
                            context
                            && Object.keys(context).length >= 1
                            && user
                            && Object.keys(user).length >= 1
                            && ((shopCart
                                && Object.keys(shopCart).length >= 1)
                                || (shopCartCore
                                    && Object.keys(shopCartCore).length >= 1))
                        );
                    }),
                    first()
                );
            }),
            map(([[context, user, shopCart, shopCartCore, action], [order, url, currencyInfo]]) => {
                const { section, page, title } = action;
                const data = imEventWrapperGetPageView(
                    section,
                    page,
                    title,
                    context,
                    user,
                    shopCart || shopCartCore,
                    url,
                    this.window,
                    currencyInfo,
                    order,
                    action.category
                );
                return {
                    pageViewObject: data,
                    eventFrom: action.eventFrom,
                };
            }),
            tap(({ pageViewObject }) => {
                imEventPageView([pageViewObject], {});
            }),
            map(({ eventFrom, pageViewObject }) => {
                return AnalyticsActions.LoadEventSuccess({
                    payload: {
                        type: 'PageViewEvent',
                        dataObject: pageViewObject,
                        eventFrom,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public customPageViewEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AnalyticsActions.CustomPageViewEvent),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            switchMap((action) => {
                return combineLatest([
                    this.getContext$,
                    this.getUser$,
                    this.store.select(getShopCart),
                    this.store.select(getShopCartCore),
                    of(action),
                    this.store.select(getCurrentOrder),
                    this.store.select(getCurrencyInfo),
                ]).pipe(
                    filter(([context, user, shopCart, shopCartCore, __]) => {
                        return (
                            context
                            && Object.keys(context).length >= 1
                            && user
                            && Object.keys(user).length >= 1
                            && ((shopCart
                                && Object.keys(shopCart).length >= 1)
                                || (shopCartCore
                                    && Object.keys(shopCartCore).length >= 1))
                        );
                    }),
                    first()
                );
            }),
            map(([context, user, shopCart, shopCartCore, action, order, currencyInfo]) => {
                const { dataObject } = action;
                const data = imEventWrapperGetBasicPageView(
                    context,
                    user,
                    shopCart || shopCartCore,
                    this.window,
                    currencyInfo,
                    order
                );
                const response: [PageViewDataObject, EcommerceData] = [{
                    ...data,
                    ...dataObject,
                    eventFrom: action.eventFrom,
                } as PageViewDataObject, action.ecommerce];
                return response;
            }),
            tap(([dataObject, ecommerce]) => {
                imEventPageView([dataObject], ecommerce ? { ...ecommerce } : undefined);
            }),
            map(([data]) => {
                return AnalyticsActions.LoadEventSuccess({
                    payload: {
                        type: 'PageViewEvent',
                        dataObject: data,
                        eventFrom: data.eventFrom,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    // eliminar este pageview
    public checkoutPageViewAnalyticsEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AnalyticsActions.CheckoutPageView),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            concatLatestFrom(() => [
                this.getContext$,
                this.getUser$,
                this.store.select(getShopCart),
                this.store.select(getShopCartCore),
                this.store.select(getCurrentOrder),
                this.store.select(routerPublicQueries.getUrlFromRouter),
                this.store.select(getCurrencyInfo),
            ]),
            tap(([action, context, user, shopCart, shopCartCore, currentOrder, url, currencyInfo]) => {
                const {
                    section, page, title, category,
                } = action[1];
                imEventWrapperSendBasicPageView(
                    section,
                    page,
                    title,
                    context,
                    user,
                    url,
                    this.window,
                    currencyInfo,
                    category,
                    shopCart || shopCartCore,
                    currentOrder
                );
            }),
            map((data) => {
                const { eventFrom } = data[0][1];
                const dataObject = data[0][1];
                return AnalyticsActions.LoadEventSuccess({
                    payload: {
                        type: 'PageViewEvent',
                        dataObject,
                        eventFrom,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public PageviewSendSetter$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AnalyticsActions.LoadEventSuccess),
            filter(action => action.payload.type === 'PageViewEvent'),
            map(() => AnalyticsActions.UniversalPageViewIsSent({ isSent: true })),
        );
    });

    public basicPopUpView$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AnalyticsActions.BasicPopUpView),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            concatLatestFrom((action) => [
                this.getUser$,
                this.getContext$,
                this.store.select(getCategory),
                this.store.select(getCategoryProduct),
                this.getCategoriesNavigation$,
                this.store.select(selectUserIsPrivateSale),
                of(action),
            ]),
            map(([actionData, user, context, categoryData, categoryProductId, categoriesNavigation, showPrivateSale]) => {
                try {
                    const category: Category | MenuCategory = imPopUpGetCategoryData(
                        categoryData,
                        categoryProductId,
                        categoriesNavigation,
                    );
                    const {
                        section,
                        pageType,
                        pageTitle,
                        products,
                        items,
                        parentType,
                        eventFrom,
                    } = actionData;
                    const product: Product = products?.length ? products[0] : undefined;

                    imPopUpSendBasic(
                        section,
                        pageType,
                        pageTitle,
                        context,
                        category,
                        user,
                        items,
                        product,
                        parentType,
                        showPrivateSale,
                        this.storageService,
                        this.window
                    );

                    const dataObject = {};
                    return AnalyticsActions.LoadEventSuccess({
                        payload: {
                            type: 'BasicPopUpView',
                            dataObject,
                            eventFrom,
                        },
                    });
                } catch (e) {
                    sendObservabilityLog(e, 'basicPopUpView$');
                    return AnalyticsActions.LoadEventError();
                }
            }),
        );
    });

    public customPopUpView$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AnalyticsActions.CustomPopUpView),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            concatLatestFrom(() => [this.getUser$, this.getContext$]),
            tap(([actionData, user, context]) => {
                const {
                    popupData,
                    eccomerce,
                } = actionData;
                imPopUpSendCustom(context, user, popupData, eccomerce, this.window);
            }),
            map(([effectAction]) => {
                const { eventFrom } = effectAction;
                const dataObject = {};
                return AnalyticsActions.LoadEventSuccess({
                    payload: {
                        type: 'BasicPopUpView',
                        dataObject,
                        eventFrom,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public sendErrorPagePageview$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetPage),
            filter(({ page }) => checkMatch(page, applicationErrorPagesMap)),
            map(({ page }) => AnalyticsActions.PageViewEvent({
                page,
                title: '',
                section: 'error',
                eventFrom: EventFrom.error,
            }))
        );
    });

    public loadAnalyticsSidebarModule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidebarCommands.OPEN_SIDEBAR, SidebarCommands.OPEN_SIDEBAR_CUSTOM),
            // eslint-disable-next-line no-secrets/no-secrets
            concatLatestFrom(() => this.store.select(getIsModuleLoaded('AnalyticsSidebarModule'))),
            filter(([_, analyticsSidebarLoaded]) => !analyticsSidebarLoaded),
            map(() => {
                return LoadModule(
                    { // eslint-disable-next-line no-secrets/no-secrets
                        module: 'AnalyticsSidebarModule',
                        path: () => import('@shared/analytics/analytics-sidebar.module').then((m) => m.AnalyticsSidebarModule),
                    }
                );
            }),
        );
    });

    public SendHomePageViewAnalytics$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HomeLoadedAnalytics),
            switchMap(() =>
                this.store.select(getIsModuleLoaded('AnalyticsHomeModule')).pipe(
                    filter(isModuleLoaded => isModuleLoaded),
                    take(1),
                    map(() => SendHomePageView())
                ))
        ));
    /* Mientras se realice una recarga de la página después de cambiar la configuración de cookies este efecto no
    será necesario y provocará pageview duplicados */
    // public pageViewOnLoadGTM$ = createEffect(() => {
    //     return this.actions$.pipe(
    //         ofType(InitTertiaryLoadSuccess),
    //         switchMap(() => this.store.select(isGTMLoaded).pipe(distinctUntilChanged())),
    //         filter((isLoaded) => !!isLoaded),
    //         withLatestFrom(this.store.select(getUniversalPageViewIsSent)),
    //         filter(([_, isSend]) => !isSend),
    //         withLatestFrom(this.store.select(getPage)),
    //         map(([_, page]) => {
    //             switch (page) {
    //                 case 'ItxHomePage':
    //                     return SendHomePageView();
    //                 case 'ItxCategoryPage':
    //                     return SendParrillaPageView();
    //                 case 'ItxProductPage':
    //                     return SendProductPagePageView();
    //                 case 'ItxAngularShopcartPage':
    //                     return AnalyticsActions.ControlCartPageviewAnalytics({
    //                         cf: AnalyticsCartCF.shopcart,
    //                         category: AnalyticsCartCategory.cesta,
    //                     });
    //                 case 'ItxAngularAuthenticationPage':
    //                     return AnalyticsActions.PageViewEvent({
    //                         page: 'acceso',
    //                         title: 'login',
    //                         section: 'perfil_usuario',
    //                         eventFrom: EventFrom.authentication,
    //                     });
    //                 case 'ItxAngularLookbookPage':
    //                     return SendLookBookPageView();
    //                 default:
    //                     return NoOpAction();
    //             }

    //             /* const parsedSlug = slug.toLowerCase();
    //             if (parsedSlug.includes('women')) {
    //                 return HomePageView({ page: 'pagina_inicio_women' });
    //             } if (parsedSlug.includes('men')) {
    //                 return HomePageView({ page: 'pagina_inicio_men' });
    //             } if (parsedSlug.includes('bg')) {
    //                 return HomePageView({ page: 'pagina_inicio_beauty' });
    //             } */
    //         }),
    //     );
    // });
}
